import { useEffect, useState } from "react";
import { Contrat, ContratInfo } from "../../model/Model";
import { ajouterContrat, getContrats, supprimerContrat, modifierContrat, telechargerContratPdf } from "../../service/contrats/contrats.service";
import { AjouterContratPopup } from "./AjouterContrat";
import ContratTemplates from "./ContratTemplates";

type TabType = 'contrats' | 'templates';

function Contrats(){
    const [contrats, setContrats] = useState<ContratInfo[]>([]);
    const [seen, setSeen] = useState(false);
    const [contratId, setContratId] = useState<number | undefined>(undefined);
    const [menuOpen, setMenuOpen] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<TabType>('contrats');

    useEffect(() => {
        async function fetchData() {
            const data = await getContrats();
            console.log(data);
            setContrats(data);
        }
        fetchData();
    }, []);

    function togglePopup() {
        setSeen(!seen);
        setContratId(undefined);
    }

    const closePopup = () => {
        setSeen(false);
        setContratId(undefined);
    };

    async function ajouterModifier(contrat: Contrat): Promise<void> {
        try {
            if (contrat.contratId) {
                await modifierContrat(contrat);
            } else {
                await ajouterContrat(contrat);
            }
        } catch (error) {
            console.log(error);
        } finally {
            const contrats = await getContrats();
            setContrats(contrats);
            setSeen(false);
        }
    }

    const getStatutBadge = (statut: string) => {
        const baseClasses = "inline-flex px-2 py-1 text-xs font-semibold rounded-full";
        switch (statut) {
            case 'ACTIF':
                return `${baseClasses} bg-green-100 text-green-800`;
            case 'EXPIRE':
                return `${baseClasses} bg-yellow-100 text-yellow-800`;
            case 'RESILIÉ':
                return `${baseClasses} bg-red-100 text-red-800`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-800`;
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Non définie';
        return new Date(dateString).toLocaleDateString('fr-FR');
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    useEffect(() => {
        // Effect when contrats change
    }, [contrats]);

    useEffect(() => {
        const handleClickOutside = () => {
            if (menuOpen !== null) {
                setMenuOpen(null);
            }
        };
        
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [menuOpen]);

    
    async function handleSupprimer(id: number | undefined) {
            if (id === undefined) return;
            try {
                await supprimerContrat(id);
                const contrats = await getContrats();
                setContrats(contrats);
            } catch (error) {
                console.log(error);
            }
    }
    
    function handleModifier(id: number | undefined) {
        setContratId(id);
        setSeen(true);
    }

    async function handleTelechargerPdf(id: number | undefined) {
        if (id === undefined) return;
        try {
            await telechargerContratPdf(id);
        } catch (error) {
            console.log(error);
            alert('Erreur lors du téléchargement du PDF');
        }
    }
    
    
    const toggleMenu = (id: number | undefined) => {
        if (id !== undefined) {
            setMenuOpen(menuOpen === id ? null : id);
        }
    };

    const handleMenuClick = (e: React.MouseEvent, id: number | undefined) => {
        e.stopPropagation();
        toggleMenu(id);
    };

    return(
        <div className="pt-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Navigation par onglets */}
                <div className="mb-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('contrats')}
                            className={`${
                                activeTab === 'contrats'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 cursor-pointer`}
                        >
                            <div className="flex items-center space-x-2">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>Liste des contrats</span>
                                {contrats.length > 0 && (
                                    <span className="bg-blue-100 text-blue-600 py-0.5 px-2 rounded-full text-xs font-medium">
                                        {contrats.length}
                                    </span>
                                )}
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('templates')}
                            className={`${
                                activeTab === 'templates'
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 cursor-pointer`}
                        >
                            <div className="flex items-center space-x-2">
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                </svg>
                                <span>Templates</span>
                            </div>
                        </button>
                    </nav>
                </div>

                {/* Contenu des onglets */}
                {activeTab === 'contrats' && (
                    <>
                        <div className="mb-6">
                            <button 
                                onClick={togglePopup}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200 ease-in-out transform hover:scale-105 shadow-md"
                            >
                                + Créer un contrat
                            </button>
                            {seen ? <AjouterContratPopup ajouterModifier={ajouterModifier} onClose={closePopup} contratId={contratId}/> : null}
                        </div>

                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                <h2 className="text-xl font-semibold text-gray-800">Liste des contrats</h2>
                                <p className="text-sm text-gray-600 mt-1">Association entre biens et locataires</p>
                            </div>
                            
                            {contrats?.length > 0 ? (
                                <div className="overflow-x-auto  pb-20">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    ID
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Bien
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Locataire
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Période
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Loyer
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Charges
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Statut
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Actions
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {contrats?.map((contrat, index) => (
                                                <tr key={contrat.contratId || index} className="hover:bg-gray-50 transition-colors duration-200">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                        {contrat.contratId || index + 1}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <div className="flex flex-col">
                                                                {contrat.bienInfo || 'Bien non trouvé'}
                                                            
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <div className="flex flex-col">
                                                            {contrat.locataireInfo || 'Bien non trouvé'}
                                                            
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                        <div className="flex flex-col">
                                                            <span>Du {formatDate(contrat.dateDeDebut)}</span>
                                                            {contrat.dateDeFin && (
                                                                <span className="text-xs text-gray-500">
                                                                    Au {formatDate(contrat.dateDeFin)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                                        {formatCurrency(contrat.montantLoyer)}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                        {contrat.montantCharges ? formatCurrency(contrat.montantCharges) : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <span className={getStatutBadge(contrat.statut)}>
                                                            {contrat.statut}
                                                        </span>
                                                    </td>
                                                     <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                        <div className="relative inline-block text-left">
                                                            <button 
                                                                onClick={(e) => handleMenuClick(e, contrat.contratId ?? undefined)}
                                                                className="text-gray-600 hover:text-gray-900 p-2 rounded-full hover:bg-gray-100 transition-colors"
                                                                title="Actions"
                                                            >
                                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                                                                </svg>
                                                            </button>
                                                            
                                                            {menuOpen === (contrat.contratId ?? undefined) && (
                                                                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                                                    <div className="py-1">
                                                                        <button
                                                                            className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors flex items-center"
                                                                            onClick={() => {
                                                                                setMenuOpen(null);
                                                                                handleTelechargerPdf(contrat.contratId ?? undefined);
                                                                            }}
                                                                        >
                                                                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                            </svg>
                                                                            Télécharger PDF
                                                                        </button>
                                                                        <button
                                                                            className="w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50 transition-colors flex items-center"
                                                                            onClick={() => {
                                                                                setMenuOpen(null);
                                                                                handleModifier(contrat.contratId ?? undefined);
                                                                            }}
                                                                        >
                                                                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                            </svg>
                                                                            Modifier
                                                                        </button>
                                                                        <button
                                                                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center"
                                                                            onClick={() => {
                                                                                setMenuOpen(null);
                                                                                handleSupprimer(contrat.contratId ?? undefined);
                                                                            }}
                                                                        >
                                                                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                            </svg>
                                                                            Supprimer
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="px-6 py-12 text-center">
                                    <div className="text-gray-400 mb-4">
                                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun contrat trouvé</h3>
                                    <p className="text-gray-500">Cliquez sur "Créer un contrat" pour associer un bien à un locataire.</p>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {activeTab === 'templates' && (
                    <ContratTemplates />
                )}
            </div>
        </div>
    )
}
export default Contrats;